import { useState, useEffect } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

// Phase 8.5-04 UX-01 — tier-3 destroy primitive (ADR-014).
// Operator must type the resource's identifier (campaign.name, account.steamLogin, ...)
// exactly to enable the destroy button. Strict equality after .trim() — matches
// backend's confirmation_mismatch validation.
export interface DestroyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title at the top of the dialog. */
  title: string;
  /** Free-form warning text — describe the blast radius (e.g. cascade behaviour). */
  description: string;
  /** Resource type, used in input label. */
  resourceLabel: string;
  /** The exact string operator must type. Backend compares strict-equal post-trim. */
  expectedConfirmation: string;
  onConfirm: () => void;
  isPending?: boolean;
}

export const DestroyDialog = ({
  open,
  onOpenChange,
  title,
  description,
  resourceLabel,
  expectedConfirmation,
  onConfirm,
  isPending = false,
}: DestroyDialogProps) => {
  const [typed, setTyped] = useState('');

  // Reset typed value whenever the dialog re-opens — avoids accidental
  // confirmation if the operator opens the same dialog twice in a row.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const matches = typed.trim() === expectedConfirmation.trim();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="destroy-confirm-input">
            Введите {resourceLabel}{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">
              {expectedConfirmation}
            </code>{' '}
            для подтверждения
          </Label>
          <Input
            id="destroy-confirm-input"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
            }}
            placeholder={expectedConfirmation}
            disabled={isPending}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            disabled={!matches || isPending}
            onClick={(event) => {
              event.preventDefault();
              if (matches) onConfirm();
            }}
            className={cn(buttonVariants({ variant: 'destructive' }))}
          >
            {isPending ? 'Уничтожение…' : 'Уничтожить'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
